import { useEffect, useMemo, useState } from "react";
import type { BusinessScenario, SkillManifest, TestRun } from "@ai-sales/contracts";
import { api } from "./api.js";
import { ReleaseEvidenceSummary } from "./ReleaseEvidenceSummary.js";
import { SparklesIcon } from "./icons.js";

const statusLabels: Record<BusinessScenario["status"], string> = {
  draft: "草稿待测试",
  validating: "正在校验",
  testing: "正在测试",
  trial_ready: "可开始试用",
  trial: "试用中",
  published: "已发布",
  archived: "已停用"
};

const operationLabels = { query: "查询", aggregate: "统计", create: "创建", update: "更新", delete: "删除" } as const;

export function BusinessScenarioLifecycle(props: {
  scenarios: BusinessScenario[];
  skills: SkillManifest[];
  testRuns: TestRun[];
  busy: string;
  canManage: boolean;
  runAction: (name: string, action: () => Promise<string>) => Promise<void>;
  onCreateScenario?: () => void;
}) {
  const [selectedId, setSelectedId] = useState("");
  const [assignToAll, setAssignToAll] = useState(true);
  const selected = props.scenarios.find((scenario) => scenario.id === selectedId) ?? props.scenarios[0];
  useEffect(() => {
    if (selected && selected.id !== selectedId) setSelectedId(selected.id);
  }, [selected?.id, selectedId]);
  const activeSkill = useMemo(() => selected
    ? props.skills.find((skill) => skill.key === selected.linkedSkillKey && skill.version === selected.linkedSkillVersion)
      ?? props.skills.find((skill) => skill.key === selected.linkedSkillKey)
    : undefined,
  [props.skills, selected]);
  if (!props.scenarios.length) return <section className="admin-card scenario-lifecycle-empty">
    <SparklesIcon />
    <div><h2>还没有特殊场景</h2><p>系统基础能力已由 Org 分析提供。只有固定审批、专用字段映射、外部集成或可复用业务规则，才需要创建场景。</p></div>
    {props.canManage && props.onCreateScenario ? <button className="button button--primary" type="button" onClick={props.onCreateScenario}>使用向导创建场景</button> : null}
  </section>;

  return <div className="admin-workspace admin-workspace--product scenario-lifecycle">
    <aside className="skill-list-panel">
      {props.canManage && props.onCreateScenario ? <button className="button button--primary button--full" type="button" onClick={props.onCreateScenario}>使用向导创建场景</button> : null}
      <p className="list-count">共 {props.scenarios.length} 个业务场景</p>
      <div className="skill-list">{props.scenarios.map((scenario) => <button type="button" key={scenario.id} className={scenario.id === selected?.id ? "skill-row skill-row--active" : "skill-row"} onClick={() => setSelectedId(scenario.id)}>
        <div><strong>{scenario.name}</strong><span>{scenario.businessGoal}</span></div>
        <small>{scenario.linkedSkillVersion} · {statusLabels[scenario.status]}</small>
      </button>)}</div>
    </aside>
    {selected ? <section className="skill-editor">
      <header className="skill-editor__header">
        <div><p className="eyebrow">业务场景生命周期</p><h2>{selected.name}</h2><p>{selected.linkedSkillVersion} · {statusLabels[selected.status]}</p></div>
        <span className={selected.status === "published" ? "connection-pill connection-pill--ok" : selected.status === "archived" ? "connection-pill connection-pill--warn" : "connection-pill"}>{statusLabels[selected.status]}</span>
      </header>
      <div className="editor-grid">
        <div className="editor-form scenario-lifecycle__content">
          <section><h3>业务目标</h3><p>{selected.businessGoal}</p></section>
          <section><h3>用户常用说法</h3><ul>{selected.exampleRequests.map((request) => <li key={request}>{request}</li>)}</ul></section>
          <section><h3>受控能力范围</h3><div className="scenario-lifecycle__capabilities">{selected.capabilities.map((capability) => <article key={capability.objectApiName}><strong>{capability.objectApiName}</strong><span>{capability.operations.map((operation) => operationLabels[operation]).join(" · ")}</span></article>)}</div></section>
          {selected.status === "archived" && selected.readiness.blockers.length ? <div className="admin-warning-list">{selected.readiness.blockers.map((blocker) => <p key={blocker}>{blocker}</p>)}</div> : null}
        </div>
        <aside className="validation-panel">
          <section><h3>当前版本检查</h3>{activeSkill ? <ReleaseEvidenceSummary skill={activeSkill} runs={props.testRuns} /> : <p className="muted">尚无可读取的当前技能版本。</p>}</section>
          <section><h3>发布与生效</h3><p className="muted">发布前必须完成真实测试和试用。停用后新请求立即拒绝，已有审计记录保留。</p>
            {props.canManage ? <div className="scenario-lifecycle__actions">
              {activeSkill?.status === "draft" ? <button className="button button--primary" type="button" disabled={Boolean(props.busy)} onClick={() => void props.runAction(`scenario-test-${selected.id}`, async () => {
                const run = await api.testSkill(activeSkill.key);
                return run.status === "passed" ? `${run.passed} 项自动测试全部通过，可以进入试用。` : `${run.failed} 项测试未通过，请先修正后再发布。`;
              })}>运行自动测试</button> : null}
              {activeSkill?.status === "trial_ready" ? <button className="button button--primary" type="button" disabled={Boolean(props.busy)} onClick={() => void props.runAction(`scenario-trial-${selected.id}`, async () => {
                await api.startSkillTrial(activeSkill.key);
                return `${selected.name} 已进入试用，尚未向正式用户开放。`;
              })}>开始试用</button> : null}
              {activeSkill?.status === "trial" ? <>
                <label className="scenario-publish-assignment"><input type="checkbox" checked={assignToAll} onChange={(event) => setAssignToAll(event.target.checked)} />发布后分配给当前全部有效 AI 用户</label>
                <button className="button button--primary" type="button" disabled={Boolean(props.busy)} onClick={() => void props.runAction(`scenario-publish-${selected.id}`, async () => {
                  const result = await api.publishBusinessScenario(selected.id, assignToAll);
                  return `${result.scenario.name} 已发布${assignToAll ? `，并分配给 ${result.assignedUserCount} 位有效 AI 用户` : "，尚未批量分配用户"}。`;
                })}>发布场景</button>
              </> : null}
              {selected.status === "published" ? <button className="button button--danger" type="button" disabled={Boolean(props.busy)} onClick={() => void props.runAction(`scenario-disable-${selected.id}`, async () => {
                await api.disableBusinessScenario(selected.id);
                return `${selected.name} 已停用，新请求将立即停止使用该场景。`;
              })}>停用场景</button> : null}
              {selected.status === "archived" ? <button className="button button--primary" type="button" disabled={Boolean(props.busy)} onClick={() => void props.runAction(`scenario-enable-${selected.id}`, async () => {
                await api.enableBusinessScenario(selected.id);
                return `${selected.name} 已通过当前 Org 依赖校验并恢复使用。`;
              })}>重新校验并启用</button> : null}
            </div> : <p className="muted">当前账号只有查看权限。</p>}
          </section>
          <details className="scenario-details"><summary>技术信息</summary><div className="summary-list"><div className="summary-row"><span>运行技能</span><strong>{selected.linkedSkillKey}</strong></div><div className="summary-row"><span>绑定快照</span><strong>{selected.sourceSnapshotId.slice(0, 8)}</strong></div></div></details>
        </aside>
      </div>
    </section> : null}
  </div>;
}
